import { Button, Tooltip, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useRemovePatientTab } from "@/features/PatientTabs/hooks/useRemovePatientTab";
import { useMessageStatusChange } from "@/hooks/useMessageStatusChange";
import { MessageStatus } from "@/hooks/useMessageStatusChange";
import { usePinPatient } from "@/hooks/usePinPatient";
import { useUnpinPatient } from "@/hooks/useUnpinPatient";
import { trackEvent } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Archive, Pin } from "lucide-react";

interface RoutineMessageActionsProps {
  patientId: string;
  isPinned: boolean;
  isLive?: boolean;
}

export const RoutineMessageActions = ({
  patientId,
  isPinned,
  isLive = false,
}: RoutineMessageActionsProps) => {
  const pinPatient = usePinPatient(patientId);
  const unpinPatient = useUnpinPatient(patientId);
  const messageStatusChange = useMessageStatusChange(patientId);
  const navigate = useNavigate();
  const search = useSearch({ from: "/patient/$patientId" });
  const encounterId = search.encounterId as string;
  const removePatientTab = useRemovePatientTab(true);

  // Only show buttons if we have an encounter ID and it's not a live patient
  if (isLive || encounterId) {
    return null;
  }

  const handleClickPin = async () => {
    trackEvent(TRACKERS.PIN_ASYNC_MESSAGE);
    if (isPinned) {
      try {
        await unpinPatient.mutateAsync({ patientId });
      } catch (error) {
        toast.error("Unable to unpin patient", {
          id: "patient-bar-unpin-patient",
        });
      }
    } else {
      try {
        await pinPatient.mutateAsync({ patientId });
      } catch (error) {
        toast.error("Unable to pin patient", {
          id: "patient-bar-pin-patient",
        });
      }
    }
  };

  const handleClickArchive = async () => {
    try {
      await messageStatusChange.mutateAsync({
        patientId,
        statusChangeAction: MessageStatus.MARK_ARCHIVED,
      });
      await removePatientTab.mutateAsync({
        patientId,
        encounterId,
      });
      // Show success message and redirect to messages page after successful archiving
      toast.success("Message archived successfully", {
        id: "patient-bar-archive-success",
      });
      navigate({ to: "/messages" });
    } catch (error) {
      toast.error("Unable to archive patient", {
        id: "patient-bar-archive-patient",
      });
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Tooltip
        side="bottom"
        tooltipContentText={isPinned ? "Unpin" : "Pin"}
        triggerClassName="leading-none"
      >
        <Button
          isLoading={pinPatient.isPending}
          variant="icon"
          data-testid="pin-patient"
          type="button"
          onClick={handleClickPin}
          className="p-1"
        >
          <Pin
            data-testid="pin-icon"
            className={cn("w-3.5 text-link", isPinned && "fill-link")}
          />
        </Button>
      </Tooltip>

      <Tooltip
        side="bottom"
        tooltipContentText="Send to Archive"
        triggerClassName="leading-none"
      >
        <Button
          isLoading={messageStatusChange.isPending}
          variant="icon"
          data-testid="archive-patient"
          type="button"
          onClick={handleClickArchive}
          className="p-1"
        >
          <Archive className="w-3.5 text-link" />
        </Button>
      </Tooltip>
    </div>
  );
};
